import { FC, useCallback, useEffect } from 'react';
import { useSheetSelection } from '../../contexts/sheetSelection';
import { Text } from '../Typography';
import MessageComposer from '../MessageComposer/MessageComposer';
import { MessageList } from './MessageList';
import { useMessages } from '@/hooks/useMessages';
import { useChats } from '@/hooks/useChats';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Message } from '@/types/chat';
import { WorkflowCard } from '../WorkflowCard/WorkflowCard';

const SCENARIO_WORKFLOW = 'scenario_analysis';
const STAKEHOLDER_WORKFLOW = 'stakeholder_directive';

const WORKFLOWS = [
  {
    title: 'Scenario Analysis',
    description: 'Analyze how external events impact your portfolio companies',
    prompt:
      'Run a scenario analysis on how current external events are impacting our portfolio companies.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    title: 'Stakeholder Directive',
    description: 'Draft targeted communications to portfolio company CEOs',
    prompt:
      'Draft stakeholder communications for our portfolio company CEOs based on the latest scenario analysis.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
  },
  {
    title: 'Board Decisions',
    description: 'Model board responses and design governance submissions',
    prompt:
      'Model board responses and design governance submissions for the upcoming board decisions.',
    icon: (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="2" x2="12" y2="6" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <line x1="8" y1="6" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="18" />
      </svg>
    ),
  },
];

export const Chat: FC = () => {
  const { selectedSheetId, setSelectedSheetId, selectedCategoryIds } = useSheetSelection();
  const { chatId, accountId } = useParams<{ chatId?: string; accountId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isCategoryMode = selectedCategoryIds.length > 0;
  const numericChatId = chatId && chatId !== 'new' ? Number(chatId) : null;
  const hasValidChatId = numericChatId != null && !Number.isNaN(numericChatId);
  const currentCategoryIdsParam = searchParams.get('categoryIds') ?? '';
  const selectedWorkflow = searchParams.get('workflow');
  const isScenarioWorkflow = selectedWorkflow === SCENARIO_WORKFLOW;
  const isStakeholderWorkflow = selectedWorkflow === STAKEHOLDER_WORKFLOW;
  const { messages, regenerate, sendStreaming, cancelStreaming } = useMessages(
    accountId,
    hasValidChatId ? numericChatId : null,
    { skipHistoryLoad: isCategoryMode && !hasValidChatId }
  );
  const { remove: removeChat } = useChats(accountId, { skipLoad: isCategoryMode });
  const canChat = isCategoryMode || selectedSheetId != null;

  const buildSearch = (extras?: Record<string, string | undefined>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(extras ?? {}).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    const next = params.toString();
    return next ? `?${next}` : '';
  };

  const selectWorkflow = (workflow: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('workflow', workflow);
      return next;
    });
  };

  const handleSubmit = async (text: string, files?: File[]) => {
    if (!accountId) return;

    const workflow = selectedWorkflow || undefined;

    if (isCategoryMode) {
      let navigated = false;
      const ensureNavigation = (targetChatId: number | null | undefined) => {
        if ((chatId && chatId !== 'new') || navigated) return;
        if (targetChatId == null || Number.isNaN(targetChatId)) return;
        navigated = true;
        const categoryIdsValue = currentCategoryIdsParam || selectedCategoryIds.map((id) => String(id)).join(',');
        navigate(
          `/account/${accountId}/chat/${targetChatId}${buildSearch({
            categoryIds: categoryIdsValue,
            workflow,
          })}`
        );
      };

      const newChatId = await sendStreaming(text, null, {
        categoryIds: selectedCategoryIds,
        workflow,
        files,
        onChatResolved: (resolvedChatId) => {
          ensureNavigation(resolvedChatId);
        },
      });

      ensureNavigation(newChatId);
    } else {
      if (!selectedSheetId) return;

      let navigated = false;
      const ensureNavigation = (targetChatId: number | null | undefined) => {
        if ((chatId && chatId !== 'new') || navigated) return;
        if (targetChatId == null || Number.isNaN(targetChatId)) return;
        navigated = true;
        navigate(`/account/${accountId}/chat/${targetChatId}${buildSearch({ workflow })}`);
      };

      const newChatId = await sendStreaming(text, selectedSheetId, {
        workflow,
        files,
        onChatResolved: (resolvedChatId) => {
          ensureNavigation(resolvedChatId);
        },
      });

      ensureNavigation(newChatId);
    }
  };

  const handleRegenerate = async (message: Message) => {
    if (!accountId) return;
    if (!isCategoryMode && !selectedSheetId) return;

    const promptToSend = message.title;
    if (!promptToSend) return;

    if (isCategoryMode) {
      await regenerate(promptToSend, null, message.id, message.title || '', {
        categoryIds: selectedCategoryIds,
        workflow: selectedWorkflow || undefined,
      });
    } else {
      await regenerate(promptToSend, selectedSheetId!, message.id, message.title || '', {
        workflow: selectedWorkflow || undefined,
      });
    }
  };

  const handleDeleteChat = useCallback(async () => {
    if (!chatId || !accountId) return;
    try {
      const success = await removeChat(chatId);
      if (success) {
        setSelectedSheetId(null);
        navigate(`/account/${accountId}`);
      }
    } catch (error) {
      if (import.meta.env?.DEV) {
        console.error('Failed to delete chat', error);
      }
    }
  }, [chatId, accountId, navigate, removeChat, setSelectedSheetId]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (!chatId) return;
      const isDeleteKey = event.key === 'Backspace' || event.key === 'Delete';
      const isModifierHeld = event.metaKey || event.ctrlKey;
      if (!isDeleteKey || !isModifierHeld || event.repeat) return;
      event.preventDefault();
      void handleDeleteChat();
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [chatId, handleDeleteChat]);

  const handleCancelStreaming = () => {
    cancelStreaming();
  };

  if (!accountId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Text variant="text-lg-bold" className="text-grey-medium">
            Select an account to start
          </Text>
          <Text variant="text-md-regular" className="text-grey-medium">
            Use the account switcher in the sidebar to choose a workspace first.
          </Text>
        </div>
      </div>
    );
  }

  const isEmpty = !canChat || messages.length === 0;
  const showScenarioWelcome = isEmpty && canChat && isScenarioWorkflow;
  const showStakeholderWelcome = isEmpty && canChat && isStakeholderWorkflow;
  const showWorkflowWelcome = showScenarioWelcome || showStakeholderWelcome;

  const workflowHandlers: Record<number, string> = {
    0: SCENARIO_WORKFLOW,
    1: STAKEHOLDER_WORKFLOW,
  };

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6">
        <img
          src="/mep-logo.png"
          alt="Ministry of Economy and Planning"
          className="h-[91px] w-auto object-contain"
        />
        <h2 className="text-[24px] font-bold text-grey-black tracking-[0.3px] text-center">
          {showScenarioWelcome
            ? 'Scenario Analysis Mode'
            : showStakeholderWelcome
              ? 'Stakeholder Directive Mode'
              : 'Welcome to the MEP Executives Chat'}
        </h2>
        {showScenarioWelcome && (
          <div className="w-full max-w-[560px] bg-white border border-peach-1 rounded-2xl p-6 shadow-subtle">
            <p className="text-md text-grey-black font-medium mb-3">Welcome to the MEP scenario analyser.</p>
            <ol className="list-decimal pl-5 space-y-2 text-md leading-[24px] text-grey-hover">
              <li>Paste a news link/article or write a short scenario summary.</li>
              <li>Provide start and end dates when this scenario affects the business.</li>
              <li>Choose one case: Best Case, Base Case, or Worst Case.</li>
            </ol>
          </div>
        )}
        {showStakeholderWelcome && (
          <div className="w-full max-w-[560px] bg-white border border-peach-1 rounded-2xl p-6 shadow-subtle">
            <p className="text-md text-grey-black font-medium mb-3">Welcome to the MEP stakeholder directive tool.</p>
            <ol className="list-decimal pl-5 space-y-2 text-md leading-[24px] text-grey-hover">
              <li>Specify the topic or context for the communication.</li>
              <li>Select the target audience (e.g., portfolio company CEOs).</li>
              <li>Define the tone and urgency level.</li>
              <li>Attach additional information that needs to be taken into account to generate an email.</li>
            </ol>
          </div>
        )}
        {!showWorkflowWelcome && canChat && (
          <div className="flex gap-4 justify-center">
            {WORKFLOWS.map((wf, index) => (
              <WorkflowCard
                key={wf.title}
                icon={wf.icon}
                title={wf.title}
                description={wf.description}
                disabled={!(index in workflowHandlers)}
                onClick={() => {
                  const workflow = workflowHandlers[index];
                  if (workflow) selectWorkflow(workflow);
                }}
              />
            ))}
          </div>
        )}
        <MessageComposer placeholder="Ask something" onSubmit={handleSubmit} autoFocus />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <MessageList
          messages={messages}
          onRegenerate={handleRegenerate}
          onCancel={handleCancelStreaming}
          onSubmitPrompt={handleSubmit}
        />
      </div>
      <MessageComposer placeholder="Ask something" onSubmit={handleSubmit} />
    </div>
  );
};

export default Chat;
